"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, Search, X } from "lucide-react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import Fuse from "fuse.js";
import type { BlogPostMeta } from "@/lib/blog";
import styles from "./blog/editorial.module.css";
import { ArticleArt } from "./blog/article-art";

type Props = {
  posts: BlogPostMeta[];
  categories: string[];
  selectedCategory: string | null;
  initialQuery?: string;
};
const date = new Intl.DateTimeFormat("en-GB", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

/** Articles shown before the reader asks for more. */
const PAGE_SIZE = 12;

export function BlogCategoryView({
  posts,
  categories,
  selectedCategory,
  initialQuery = "",
}: Props) {
  const [query, setQuery] = useState(initialQuery);
  const [shown, setShown] = useState(PAGE_SIZE);
  const collectionRef = useRef<HTMLDivElement>(null);
  // A new search starts from the first page of results.
  useEffect(() => setShown(PAGE_SIZE), [query]);
  const fuse = useMemo(
    () =>
      new Fuse(posts, {
        keys: [
          { name: "title", weight: 0.5 },
          { name: "excerpt", weight: 0.25 },
          { name: "tags", weight: 0.2 },
          { name: "category", weight: 0.05 },
        ],
        threshold: 0.3,
        ignoreLocation: true,
      }),
    [posts],
  );
  const matches = query.trim()
    ? fuse.search(query.trim()).map((result) => result.item)
    : posts;
  const visible = selectedCategory
    ? matches.filter((post) => post.category === selectedCategory)
    : matches;
  function categoryUrl(category: string | null) {
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (query.trim()) params.set("q", query.trim());
    return `/blog${params.size ? `?${params}` : ""}#articles`;
  }
  return (
    <section
      id="articles"
      className={styles.browse}
      style={{ scrollMarginTop: 90 }}
      aria-labelledby="articles-title"
    >
      <div className={styles.browseHeader}>
        <h2 id="articles-title">All the field notes.</h2>
        <form
          action="/blog#articles"
          method="get"
          role="search"
          className={styles.search}
        >
          <Search size={17} aria-hidden />
          {selectedCategory ? (
            <input type="hidden" name="category" value={selectedCategory} />
          ) : null}
          <input
            name="q"
            type="search"
            aria-label="Search articles"
            placeholder="Search ideas, tools, questions…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {query ? (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => setQuery("")}
            >
              <X size={16} aria-hidden />
            </button>
          ) : null}
        </form>
      </div>
      <nav className={styles.filters} aria-label="Article categories">
        {[null, ...categories].map((category) => (
          <Link
            key={category ?? "all"}
            href={categoryUrl(category)}
            aria-current={selectedCategory === category ? "page" : undefined}
          >
            {category ?? "All topics"}
          </Link>
        ))}
      </nav>
      <p className={styles.resultCount} role="status">
        {visible.length} {visible.length === 1 ? "article" : "articles"}
        {query.trim() ? ` matching “${query.trim()}”` : ""}
      </p>
      <div ref={collectionRef} className={styles.postCollection}>
        {visible.map((post, index) => (
          <Link
            key={post.slug}
            href={`/blog/${post.slug}`}
            className={styles.postCard}
            // Later articles stay in the markup for crawlers and appear on demand.
            hidden={index >= shown}
          >
            <div className={styles.postArt}>
              <ArticleArt slug={post.slug} />
            </div>
            <div className={styles.postCopy}>
              <span className={styles.rowCategory}>
                {post.category}
                {post.format === "interactive"
                  ? " / Interactive essay"
                  : " / Field note"}
              </span>
              <h3>{post.title}</h3>
              <p>{post.excerpt}</p>
            </div>
            <span className={styles.rowMeta}>
              <time dateTime={post.date}>
                {date.format(new Date(post.date))}
              </time>
              <span>{post.readTime}</span>
              <span>{post.author.name}</span>
            </span>
            <ArrowRight size={17} aria-hidden />
          </Link>
        ))}
      </div>
      {visible.length > shown ? (
        <button
          type="button"
          className={`${styles.action} ${styles.showMore}`}
          onClick={() => {
            const firstNew = shown;
            setShown((count) => count + PAGE_SIZE);
            requestAnimationFrame(() => {
              const cards = collectionRef.current?.querySelectorAll<HTMLAnchorElement>("a");
              cards?.[firstNew]?.focus();
            });
          }}
        >
          Show more articles ({visible.length - shown} more)
        </button>
      ) : null}
      {visible.length === 0 ? (
        <p className={styles.muted}>
          Try a broader term or{" "}
          <Link href="/blog#articles">browse all topics</Link>.
        </p>
      ) : null}
    </section>
  );
}

export function BlogCategoryBrowser({
  posts,
  categories,
}: Pick<Props, "posts" | "categories">) {
  const params = useSearchParams();
  const requested = params.get("category");
  const selectedCategory =
    requested && categories.includes(requested) ? requested : null;
  const query = params.get("q") ?? "";
  return (
    <BlogCategoryView
      key={`${selectedCategory}:${query}`}
      posts={posts}
      categories={categories}
      selectedCategory={selectedCategory}
      initialQuery={query}
    />
  );
}
